import { EditorInfo, GMTInfo, Page } from './common';
import dayjs from 'dayjs';
import { useRequestImmer } from '../api/hooks';
import { assertUnreachable } from '@/utils/common';
import BigNumber from 'bignumber.js';
import { PreOrder } from './order';
import { Tag } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';

export const CardEndpoint = `/card`;

export enum CardType {
  // 次卡
  TIMES = 1,

  // 时长卡
  DURATION = 2,
}

export enum CardUserActionType {
  // 消耗
  CONSUME = 10,

  // 续期
  RENEW = 20,
}

export enum CardDurationUnit {
  // 时长卡: 时长单位
  DAY = 1,
  WEEK = 2,
  MONTH = 3,
  YEAR = 4,
}

export const getCardTypeDesc = (card: Card): string => {
  if (card.type === CardType.TIMES) {
    return '次卡';
  } else if (card.type === CardType.DURATION) {
    switch (card.durationUnit) {
      case CardDurationUnit.DAY:
        return '日卡';
      case CardDurationUnit.MONTH:
        return '月卡';
      case CardDurationUnit.WEEK:
        return '周卡';
      case CardDurationUnit.YEAR:
        return '年卡';
    }
    return assertUnreachable(card.durationUnit);
  }
};

export const getCardTypeDescTag = (card: Card) => {
  const color = card.type === CardType.TIMES ? 'purple' : 'magenta';
  return <Tag color={color}>{getCardTypeDesc(card)}</Tag>;
};

export const getCardUserStatusDescTag = (card: CardUser): React.ReactNode => {
  switch (card.status) {
    case CardUserStatus.CARD_USER_STATUS_INACTIVE:
      return (
        <Tag color={'blue'} icon={<ExclamationCircleOutlined />}>
          {getCardUserStatusDesc(card)}
        </Tag>
      );
    case CardUserStatus.CARD_USER_STATUS_ACTIVE:
      return (
        <Tag color={'green'} icon={<CheckCircleOutlined />}>
          {getCardUserStatusDesc(card)}
        </Tag>
      );
    case CardUserStatus.CARD_USER_STATUS_FINISH:
    case CardUserStatus.CARD_USER_STATUS_EXPIRED:
    case CardUserStatus.CARD_USER_STATUS_CANCELED:
      return <Tag icon={<MinusCircleOutlined />}>{getCardUserStatusDesc(card)}</Tag>;
  }

  return assertUnreachable(card.status);
};

export const getCardDurationDesc = (card: Card): string => {
  switch (card.durationUnit) {
    case CardDurationUnit.DAY:
      return `${card.duration}天`;
    case CardDurationUnit.MONTH:
      return `${card.duration}个月`;
    case CardDurationUnit.WEEK:
      return `${card.duration}周`;
    case CardDurationUnit.YEAR:
      return `${card.duration}年`;
  }
  return assertUnreachable(card.durationUnit);
};

// 次卡: 过期类型
export enum CardExpiredType {
  NO_LIMIT = 1,
  BY_DATE = 2,
  BY_DAYS = 3,
}

export const getCardExpiredDesc = (card: Card) => {
  switch (card.expiredType) {
    case CardExpiredType.NO_LIMIT:
      return `长期有效`;
    case CardExpiredType.BY_DATE:
      return `${card.expiredTime} 前`;
    case CardExpiredType.BY_DAYS:
      return `购买后 ${card.expiredDays} 天内`;
  }
  return assertUnreachable(card.expiredType);
};

/** Card */
export interface Card extends GMTInfo, EditorInfo {
  id: number;

  type: CardType;

  name: string;

  price: number;

  intro: string;

  extraIntro: string;

  detail: string;

  picUrl: string;

  isOnSale: boolean;

  isClientOnSale: boolean;

  /**次卡: 次数*/
  times: number;

  /**次卡过期类型 */
  expiredType: CardExpiredType;

  /**次卡过期时间 */
  expiredTime: string | dayjs.Dayjs;

  /**次卡过期天数 */
  expiredDays: number;

  /**时长卡: 时长数量 */
  duration: number;
  /**时长卡: 时长数量单位 */
  durationUnit: CardDurationUnit;

  sortOrder: number;

  /**关联商品 */
  specificationId: number[];
}

export enum CardUserStatus {
  CARD_USER_STATUS_INACTIVE = 1,
  CARD_USER_STATUS_ACTIVE = 2,
  CARD_USER_STATUS_EXPIRED = 3,
  CARD_USER_STATUS_FINISH = 4,
  CARD_USER_STATUS_CANCELED = 5,
}

export const getCardUserStatusDesc = (cardUser: Pick<CardUser, 'status'>) => {
  switch (cardUser.status) {
    case CardUserStatus.CARD_USER_STATUS_INACTIVE:
      return '未激活';
    case CardUserStatus.CARD_USER_STATUS_ACTIVE:
      return '使用中';
    case CardUserStatus.CARD_USER_STATUS_EXPIRED:
      return '已过期';
    case CardUserStatus.CARD_USER_STATUS_FINISH:
      return '已用完';
    case CardUserStatus.CARD_USER_STATUS_CANCELED:
      return '注销';
  }
  assertUnreachable(cardUser.status);
  return 'N/A';
};

/** CardUser */
export interface CardUser {
  id: number;
  poiId: number;
  cardId: number;
  userId: number;
  orderId: number;
  sn: string;
  times: number;
  activeTime: string;
  expiredTime: string;
  status: CardUserStatus;
  addTime: string;
  creator: string;
  deleted: boolean;
  // 卡属性
  cardName: string;
  cardType: number;
  cardPrice: number;
  cardIntro: string;
  cardTimes: number;
  cardExtraIntro: string;
  cardDetail: string;
  cardPicUrl: string;
  cardDurationUnit: number;
  cardSpecificationId: number[];
}

export const convertCardUserToCard = (card: CardUser) => {
  return {
    id: card.cardId,
    name: card.cardName,
    intro: card.cardIntro,
    picUrl: card.cardPicUrl,
    price: card.cardPrice,
    specificationId: card.cardSpecificationId,
    type: card.cardType,
    times: card.cardTimes,
  } as Card;
};

export interface CardUserLog {
  id: number;
  times: number;
  addTime: string;
  creator: string;
  type: CardUserActionType;
  changeValue?: string;
}

export const requestCardListWithPage = () =>
  useRequestImmer<{ list: Card[]; page: Page }>(
    {
      method: 'get',
      url: `${CardEndpoint}`,
    },
    {
      manual: true,
    }
  );

export const requestCardList = () =>
  useRequestImmer<Card[]>(
    {
      method: 'get',
      url: `${CardEndpoint}`,
    },
    {
      manual: true,
    }
  );

export const requestCardUserList = () =>
  useRequestImmer<{ list: CardUser[]; page: Page }>(
    {
      method: 'get',
      url: `${CardEndpoint}`,
    },
    {
      manual: true,
    }
  );

export const requestCardUserListByPoi = () =>
  useRequestImmer<{ list: CardUser[]; page: Page }>(
    {
      method: 'get',
      url: `${CardEndpoint}/user/poi`,
    },
    {
      manual: true,
    }
  );

export const requestCardUserDetail = () =>
  useRequestImmer<CardUser>(
    {
      method: 'get',
      url: `${CardEndpoint}/detail`,
    },
    {
      manual: true,
    }
  );

export const requestWriteOffCardUser = () =>
  useRequestImmer<CardUser>(
    {
      method: 'post',
      url: `${CardEndpoint}/writeOff`,
    },
    {
      manual: true,
    }
  );

export const requestActiveCardUser = () =>
  useRequestImmer<CardUser>(
    {
      method: 'post',
      url: `${CardEndpoint}/active`,
    },
    {
      manual: true,
    }
  );

export const requestCardUserLog = () =>
  useRequestImmer<
    { list: CardUserLog[]; page: Page },
    {
      params: {
        page: number;
        limit: number;
        cardUserId: number;
        actionType: CardUserActionType;
      };
    }
  >(
    {
      method: 'get',
      url: `${CardEndpoint}/cardUser/log`,
    },
    {
      manual: true,
    }
  );

export const useRenewCardUser = () =>
  useRequestImmer<CardUser, { data: string; pathVars: { sn: string } }>(
    {
      method: 'post',
      url: `${CardEndpoint}/renew/{sn}`,
    },
    {
      manual: true,
    }
  );

/**
 * 结算-待核销套餐卡信息
 */
export type WriteOffCardUserList = {
  specificationId: number;
  cardUserId: number;
  cardName: string;
  number: number;
  skuName: string;
}[];

/**
 * 结算 - 套餐卡算价
 * @param param0
 * @returns
 */
export const getCardPrice = ({
  specifications,
  userFullInfo,
  selectedCards,
}: {
  specifications: PreOrder['specifications'];
  userFullInfo: PreOrder['userFullInfo'];
  selectedCards: { [cardUserId: number]: number };
}) => {
  // 要核销的卡， 卡id: 次数
  const writeOffCardUserList: WriteOffCardUserList = [];

  // 选择了套餐卡，则扣减对应商品的价格
  let cardPrice = new BigNumber(0);
  const writeoffLeftTimesMap: { [cardId: number]: number } = Object.assign({}, selectedCards);

  // 价格降序
  const priceSortedSpecifications = [].concat(specifications).sort((a, b) => b.price - a.price);

  for (const specification of priceSortedSpecifications) {
    // 全部用户卡 -> 查找匹配商品的卡 -> 过滤次数已扣完的卡 -> 时长卡优先 -> 待抵扣的卡
    const matchesCardList = (userFullInfo?.cardList || [])
      .filter(card => card.cardSpecificationId.includes(specification.id))
      .filter(card => writeoffLeftTimesMap[card.id] > 0)
      .sort((aCard, bCard) => bCard.cardType - aCard.cardType)
      .filter(card => (selectedCards || {})[card.id]);

    //商品剩余抵扣次数
    let skuLeftTimes = specification.number;

    for (const matchesCard of matchesCardList) {
      if (skuLeftTimes === 0) {
        // 如果当前商品已全部核销，则跳过
        break;
      }
      // 已选卡的剩余可抵扣次数
      const selectedCardsLeftTimes = writeoffLeftTimesMap[matchesCard.id];

      // 如果已选卡还有剩余可抵扣次数，则可抵扣
      if (selectedCardsLeftTimes > 0) {
        // 抵扣次数： min(商品数量， 剩余可抵扣次数)
        const times = Math.min(specification.number, selectedCardsLeftTimes, skuLeftTimes);
        skuLeftTimes -= times;

        // 剩余可抵扣次数 - 本次抵扣次数
        writeoffLeftTimesMap[matchesCard.id] = writeoffLeftTimesMap[matchesCard.id] - times;
        cardPrice = cardPrice.plus(new BigNumber(specification.price).multipliedBy(times));
        writeOffCardUserList.push({
          specificationId: specification.id,
          cardUserId: Number(matchesCard.id),
          cardName: matchesCard.cardName,
          number: times,
          skuName: specification.name
            ? `${specification.goodsName}【${specification.name}】`
            : `${specification.goodsName}`,
        });
      }
    }
  }

  return {
    cardPrice,
    writeOffCardUserList,
  };
};
